// ============================================
// useTheme.js
// Hook that owns the current theme selection AND the user's per-theme
// color overrides. Exposes:
//   - theme:       the resolved theme object (base theme + overrides)
//   - themeRef:    ref always pointing at the current theme (for canvas
//                  hot-paths that read every frame without re-rendering)
//   - themeId:     current id (e.g. 'default')
//   - setThemeId:  switch themes; persists to localStorage and writes
//                  CSS custom properties on document root for any
//                  HTML/CSS surfaces that opt in via var(--cf-...)
//   - themeOverrides:  { [dottedPath]: '#rrggbb' } for the CURRENT theme
//   - setThemeColor:   (path, hex) => void   — live edit
//   - resetThemeColor: (path) => void        — revert one field
//   - resetThemeColors:() => void            — revert the whole theme
//
// Overrides are keyed by theme id, so each theme keeps its own palette
// edits and switching back and forth doesn't clobber them.
//
// Persistence is debounced (PERSIST_MS): dragging inside a native color
// input fires change events at ~60Hz and we don't want a localStorage
// write per frame. React state still updates immediately so the canvas
// repaints live.
//
// Single instance per app. Currently called once in App.jsx and the
// resolved `theme` is passed down to FootprintChartCanvas as a prop.
// ============================================
import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import {
  resolveTheme,
  DEFAULT_THEME_ID,
  THEME_STORAGE_KEY,
  THEME_OVERRIDE_STORAGE_KEY,
  addCustomTheme,
  deleteCustomTheme,
} from '../themes'

const PERSIST_MS = 250
const EMPTY = Object.freeze({})

// Write theme colors as CSS custom properties on :root so non-canvas UI
// (menus, toolbars, etc.) can opt into theming without explicit prop
// threading. Currently exposes imbalance + cell text colors; expand as
// new surfaces get themed.
const writeCssVars = (theme) => {
  if (typeof document === 'undefined') return
  const r = document.documentElement.style
  r.setProperty('--cf-imbalance-buy',  theme.imbalance.buy)
  r.setProperty('--cf-imbalance-sell', theme.imbalance.sell)
  r.setProperty('--cf-volume-profile-buy', theme.volumeProfile?.buy || '#6ea8ff')
  r.setProperty('--cf-volume-profile-sell', theme.volumeProfile?.sell || '#ff7b7b')
  r.setProperty('--cf-cell-text-dark',  theme.cell.textOnDark)
  r.setProperty('--cf-cell-text-light', theme.cell.textOnLight)
  r.setProperty('--cf-candle-bull',     theme.candle.bullBody)
  r.setProperty('--cf-candle-bear',     theme.candle.bearBody)
}

const readOverrides = () => {
  if (typeof localStorage === 'undefined') return EMPTY
  try {
    const raw = localStorage.getItem(THEME_OVERRIDE_STORAGE_KEY)
    if (!raw) return EMPTY
    const parsed = JSON.parse(raw)
    return (parsed && typeof parsed === 'object') ? parsed : EMPTY
  } catch { return EMPTY }
}

export default function useTheme() {
  const [themeId, setThemeIdState] = useState(() => {
    if (typeof localStorage === 'undefined') return DEFAULT_THEME_ID
    return localStorage.getItem(THEME_STORAGE_KEY) || DEFAULT_THEME_ID
  })

  const [overrides, setOverrides] = useState(readOverrides)

  // Throttled writer. Persistence lives in an EFFECT, not inside the state
  // updaters: React may double-invoke an updater (StrictMode dev), and a
  // localStorage write from inside one is a side effect in what is supposed
  // to be a pure function. flush() reads the latest map through a ref, so a
  // single in-flight timer always writes current state — no lost edits and
  // at most one write per PERSIST_MS no matter how fast the drag streams.
  const timerRef = useRef(null)
  const overridesRef = useRef(overrides)
  overridesRef.current = overrides
  const firstRunRef = useRef(true)

  const flush = useCallback(() => {
    timerRef.current = null
    const map = overridesRef.current
    try {
      if (!map || Object.keys(map).length === 0) localStorage.removeItem(THEME_OVERRIDE_STORAGE_KEY)
      else localStorage.setItem(THEME_OVERRIDE_STORAGE_KEY, JSON.stringify(map))
    } catch {}
  }, [])

  useEffect(() => {
    // Skip the pass triggered by the initial read — nothing has changed yet,
    // so writing back what we just loaded is pure waste.
    if (firstRunRef.current) { firstRunRef.current = false; return }
    if (timerRef.current == null) timerRef.current = setTimeout(flush, PERSIST_MS)
  }, [overrides, flush])

  // Unmount with an edit still in flight: write it now rather than drop it.
  useEffect(() => () => {
    if (timerRef.current != null) { clearTimeout(timerRef.current); timerRef.current = null; flush() }
  }, [flush])

  // resolveTheme returns the BASE object identity when the current theme
  // has no overrides, so untouched themes produce zero extra allocations
  // and every downstream `[theme]` memo stays stable.
  const theme = useMemo(() => resolveTheme(themeId, overrides), [themeId, overrides])

  // themeRef always tracks the latest resolved theme — canvas draw loops
  // read from this without React state churn. Updated synchronously on
  // setThemeId so the next paint sees the new colors.
  const themeRef = useRef(theme)
  themeRef.current = theme

  // Reflect into CSS vars whenever theme changes
  useEffect(() => {
    writeCssVars(theme)
  }, [theme])

  const setThemeId = useCallback((id) => {
    setThemeIdState(id)
    try { localStorage.setItem(THEME_STORAGE_KEY, id) } catch {}
  }, [])

  const themeOverrides = overrides[themeId] || EMPTY

  const setThemeColor = useCallback((path, hex) => {
    if (!path || !hex) return
    setOverrides(prev => {
      const cur = prev[themeId] || EMPTY
      if (cur[path] === hex) return prev
      return { ...prev, [themeId]: { ...cur, [path]: hex } }
    })
  }, [themeId])

  const resetThemeColor = useCallback((path) => {
    setOverrides(prev => {
      const cur = prev[themeId]
      if (!cur || !(path in cur)) return prev
      const rest = { ...cur }
      delete rest[path]
      const next = { ...prev }
      if (Object.keys(rest).length === 0) delete next[themeId]
      else next[themeId] = rest
      return next
    })
  }, [themeId])

  const resetThemeColors = useCallback(() => {
    setOverrides(prev => {
      if (!prev[themeId]) return prev
      const next = { ...prev }
      delete next[themeId]
      return next
    })
  }, [themeId])

  // Custom themes live in the THEMES registry (themes.js); bump a counter so
  // the picker re-lists after add/delete.
  const [themesVersion, setThemesVersion] = useState(0)

  const addTheme = useCallback((name) => {
    const id = addCustomTheme(name, themeRef.current)
    if (!id) return null
    setThemesVersion(v => v + 1)
    setThemeId(id)
    return id
  }, [setThemeId])

  const deleteTheme = useCallback((id) => {
    if (!deleteCustomTheme(id)) return
    setOverrides(prev => {
      if (!prev[id]) return prev
      const next = { ...prev }
      delete next[id]
      return next
    })
    setThemesVersion(v => v + 1)
    if (id === themeId) setThemeId(DEFAULT_THEME_ID)
  }, [themeId, setThemeId])

  return {
    theme,
    themesVersion,
    addTheme,
    deleteTheme,
    themeRef,
    themeId,
    setThemeId,
    themeOverrides,
    setThemeColor,
    resetThemeColor,
    resetThemeColors,
  }
}